import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { getPaymentTransactions } from '../../redux/services/paymentService/payment.Services';
import { ViewPlanlistByVendor } from '../../redux/services/SubscriptionServices/Subscription.Services';
import { errorToast } from '../../Utils/toast';
import { FaWallet, FaArrowLeft, FaHistory, FaLayerGroup, FaFilter, FaTimes } from 'react-icons/fa';
import { PAYMENT_STATUS_BADGE } from '../../Constant/leadConstent';
import Pagination from '../../common/Pagination';

function VendorWalletHistory() {
  const { id } = useParams();
  const vendorId = id;
  const navigate = useNavigate();
  const [tableLoading, setTableLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);

  const [activePlanData, setActivePlanData] = useState({
    name: '-',
    expiry: '-',
    totalCalls: 0,
    usedCalls: 0,
    remainingCalls: 0,
    percentage: 0,
  });
  const [purchasedPlansList, setPurchasedPlansList] = useState([]);
  const [totalPaid, setTotalPaid] = useState(0);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // 🔹 Filters
  const [filterPlan, setFilterPlan] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showFilterBar, setShowFilterBar] = useState(false);

  // ================= PLANS / WALLET CARD =================
  useEffect(() => {
    if (!vendorId) return;
    fetchVendorPlans();
  }, [vendorId]);

  const fetchVendorPlans = async () => {
    try {
      const planRes = await ViewPlanlistByVendor({ vendor_id: vendorId });
      if (planRes?.success) {
        const subsList = planRes.subscriptions || [];
        setTotalPaid(planRes.total_amount_paid || 0);

        // ✅ TOTAL USAGE CALCULATION: Sabhi subscriptions ka sum (+)
        let totalLimit = 0;
        let totalUsed = 0;
        let totalRemaining = 0;

        subsList.forEach((sub) => {
          const usageData = sub?.usage?.ai_calls || {};
          totalLimit += Number(usageData?.limit || sub?.ai_call_limit || 0);
          totalUsed += Number(usageData?.used || 0);
          totalRemaining += Number(usageData?.remaining || 0);
        });

        // Agar backend remaining 0 de raha hai, toh calculate karo
        if (totalRemaining === 0 && totalLimit > 0) {
          totalRemaining = totalLimit - totalUsed;
        }

        // ✅ Active plan ka naam aur expiry (sirf active subscription se)
        const activeSub = subsList.find((sub) => sub.status === 'active');
        
        setActivePlanData({
          name: activeSub?.plan_name || 'Multiple Plans',
          expiry: activeSub?.end_date
            ? new Date(activeSub.end_date).toLocaleDateString('en-IN')
            : 'N/A',
          totalCalls: totalLimit,       // 👈 Sum of limits
          usedCalls: totalUsed,         // 👈 Sum of used
          remainingCalls: totalRemaining, // 👈 Sum of remaining
          percentage: totalLimit > 0 ? Math.round((totalUsed / totalLimit) * 100) : 0,
        });

        // Plans History
        const plansMap = {};
        subsList.forEach((sub) => {
          const pName = sub.plan_name || 'Custom Plan';
          if (!plansMap[pName]) {
            plansMap[pName] = { name: pName, totalSpent: 0, purchaseCount: 0 };
          }
          plansMap[pName].totalSpent += Number(sub.amount_paid || 0);
          plansMap[pName].purchaseCount += 1;
        });
        setPurchasedPlansList(Object.values(plansMap));
      }
    } catch (error) {
      console.error('Plan API error:', error);
      errorToast('Failed to load vendor plan data');
    } finally {
      setInitialLoading(false);
    }
  };

  // ================= TRANSACTIONS =================
  const fetchVendorTransactions = async (pageNo = 1) => {
    try {
      setTableLoading(true);
      const payload = {
        vendor_id: vendorId,
        page: pageNo,
        per_page: limit,
        plan_name: filterPlan || undefined,
        status: filterStatus || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      };

      const txRes = await getPaymentTransactions(payload);
      setTransactions(Array.isArray(txRes?.data) ? txRes.data : []);
      setPage(txRes?.current_page || pageNo);
      setTotalPages(txRes?.last_page || 1);
      setTotalRecords(txRes?.total || 0);
    } catch (error) {
      console.error('Transaction API error:', error);
      errorToast('Failed to load vendor transactions');
      setTransactions([]);
      setTotalRecords(0);
      setTotalPages(1);
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    if (vendorId) fetchVendorTransactions(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorId, limit, filterPlan, filterStatus, startDate, endDate]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && !tableLoading) {
      setPage(newPage);
      fetchVendorTransactions(newPage);
    }
  };

  const resetFilters = () => {
    setFilterPlan('');
    setFilterStatus('');
    setStartDate('');
    setEndDate('');
  };

  // ================= HELPERS =================
  const formatAmount = (val) => {
    if (!val) return '₹0';
    return `₹${Number(val).toLocaleString('en-IN')}`;
  };

  const formatDate = (val) => {
    if (!val) return '-';
    return new Date(val).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const uniqueStatuses = ['success', 'pending', 'failed'];

  // ================= RENDER =================
  return (
    <div className="wallet-dashboard px-4 py-3">
      {/* Header */}
      <div className="wallet-header d-flex align-items-center justify-content-between mb-4">
        <div>
          <h4 className="wallet-title mb-1">Wallet & Subscriptions</h4>
          <nav className="wallet-breadcrumb d-flex align-items-center gap-2">
            <Link to="/">Home</Link>
            <span>/</span>
            <Link to="/payment-list">Payments</Link>
            <span>/</span>
            <span className="text-white">{vendorId}</span>
          </nav>
        </div>
        <button className="add-btn d-flex align-items-center gap-2" onClick={() => navigate(-1)}>
          <FaArrowLeft size={12} /> Back
        </button>
      </div>

      {/* Wallet Cards */}
      <div className="row g-3 mb-4">
        <div className="col-lg-7">
          <div className="wallet-card h-100">
            {/* Top Badge & Expiry */}
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <span className="wallet-badge-active">CURRENT ACTIVE PLAN</span>
                  <span className="wallet-expiry-text">Expires: {activePlanData.expiry}</span>
                </div>
                <h3 className="wallet-plan-name mb-0">{activePlanData.name}</h3>
              </div>
              <div className="wallet-vendor-id-box d-flex align-items-center gap-2">
                <FaWallet color="#22d3ee" size={14} />
                <span>{vendorId}</span>
              </div>
            </div>

            {/* ✅ USAGE SECTION - TOTAL SUM */}
            <div className="wallet-usage-section mt-4 pt-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="wallet-usage-label">AI CALLING CREDITS USAGE</span>
                <span className="wallet-percentage-text">
                  {activePlanData.percentage}% Used
                </span>
              </div>
              <div className="d-flex align-items-baseline gap-2 mb-2">
                <h2 className="wallet-used-count mb-0">{activePlanData.usedCalls}</h2>
                <span className="wallet-total-count">
                  / {activePlanData.totalCalls} calls
                </span>
              </div>
              <div className="wallet-progress-wrap">
                <div
                  className="wallet-progress-bar"
                  style={{ width: `${activePlanData.percentage}%` }}
                ></div>
              </div>
              <div className="d-flex justify-content-between mt-3">
                <div className="d-flex align-items-center gap-2">
                  <div className="wallet-dot wallet-dot-yellow"></div>
                  <span className="wallet-stat-text">
                    Used: <strong>{activePlanData.usedCalls}</strong>
                  </span>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <div className="wallet-dot wallet-dot-green"></div>
                  <span className="wallet-stat-text">
                    Remaining: <strong>{activePlanData.remainingCalls}</strong>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-5">
          <div className="wallet-card h-100">
            <div className="d-flex align-items-center gap-2 mb-3">
              <FaLayerGroup color="#818cf8" size={14} />
              <h6 className="wallet-section-title mb-0">Purchased Plans History</h6>
            </div>
            <div className="wallet-scroll-area">
              {purchasedPlansList.length > 0 ? (
                purchasedPlansList.map((plan, idx) => (
                  <div
                    key={idx}
                    className="wallet-plan-item d-flex justify-content-between align-items-center mb-2"
                  >
                    <div>
                      <h6 className="wallet-plan-item-name mb-0">{plan.name}</h6>
                      <span className="wallet-plan-item-count">
                        {plan.purchaseCount} time(s) purchased
                      </span>
                    </div>
                    <span className="wallet-plan-item-amount">
                      {formatAmount(plan.totalSpent)}
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-muted text-center mt-3">No plans purchased yet.</p>
              )}
            </div>
            <div className="wallet-total-footer mt-3 pt-3 d-flex justify-content-between align-items-center">
              <span className="wallet-total-label">Total Amount Paid</span>
              <span className="wallet-total-amount">{formatAmount(totalPaid)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="wallet-table-container">
        <div
          className={`wallet-table-header p-3 d-flex justify-content-between align-items-center ${
            !showFilterBar ? 'border-bottom' : ''
          }`}
        >
          <div className="d-flex align-items-center gap-2">
            <FaHistory color="#64748b" size={14} />
            <h5 className="wallet-table-title mb-0">All Transactions</h5>
            <span className="wallet-record-badge ms-2">
              {transactions.length} / {totalRecords} records
            </span>
          </div>
          <div className="d-flex align-items-center gap-2">
            {(filterPlan || filterStatus || startDate || endDate) && (
              <button className="wallet-clear-btn" onClick={resetFilters}>
                <FaTimes size={10} /> Clear
              </button>
            )}
            <button
              className={`wallet-filter-btn d-flex align-items-center gap-2 ${
                showFilterBar ? 'active' : ''
              }`}
              onClick={() => setShowFilterBar(!showFilterBar)}
            >
              <FaFilter size={10} /> Filter
            </button>
          </div>
        </div>

        {showFilterBar && (
          <div className="wallet-filter-bar p-3 d-flex flex-wrap gap-3 align-items-end">
            <div className="wallet-filter-group">
              <label>Filter by Plan</label>
              <select
                className="wallet-form-control"
                value={filterPlan}
                onChange={(e) => setFilterPlan(e.target.value)}
              >
                <option value="">All Plans</option>
                {purchasedPlansList.map((p) => (
                  <option key={p.name} value={p.name}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="wallet-filter-group">
              <label>Status</label>
              <select
                className="wallet-form-control"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="">All Status</option>
                {uniqueStatuses.map((s) => (
                  <option key={s} value={s} className="text-capitalize">
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div className="wallet-filter-group">
              <label>Start Date</label>
              <input
                type="date"
                className="wallet-form-control"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="wallet-filter-group">
              <label>End Date</label>
              <input
                type="date"
                className="wallet-form-control"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
            <div className="wallet-filter-group">
              <label>Per Page</label>
              <select
                className="wallet-form-control"
                value={limit}
                onChange={(e) => setLimit(Number(e.target.value))}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>
        )}

        <div className="table-responsive">
          <table className="table wallet-table mb-0">
            <thead>
              <tr>
                <th>Date</th>
                <th>Transaction ID</th>
                <th>Plan Purchased</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {tableLoading ? (
                <tr>
                  <td colSpan="5" className="text-center py-5">
                    <div
                      className="spinner-border text-primary"
                      style={{ width: '24px', height: '24px' }}
                    ></div>
                  </td>
                </tr>
              ) : transactions.length > 0 ? (
                transactions.map((item, index) => (
                  <tr key={item.id || index}>
                    <td className="wallet-td-date">
                      {formatDate(item.date || item.created_at)}
                    </td>
                    <td className="wallet-td-txn font-monospace">
                      {item.transaction_id || item.payment_id || '-'}
                    </td>
                    <td className="wallet-td-plan">
                      {item.plan_name || item.plan?.name || '-'}
                    </td>
                    <td className="wallet-td-amount">
                      {formatAmount(item.amount ?? item.total_amount)}
                    </td>
                    <td>
                      <span
                        className={`badge ${
                          PAYMENT_STATUS_BADGE[item.status] || 'badge-gray'
                        } text-capitalize wallet-status-badge`}
                      >
                        {item.status || '-'}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center py-5 text-muted">
                    No records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="d-flex justify-content-end mt-3">
            <Pagination
              page={page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default VendorWalletHistory;